/**
 * Date/Time utilities for IST (Indian Standard Time)
 * IST is UTC+5:30
 */

const IST_OPTIONS = {
  timeZone: 'Asia/Kolkata',
};

/**
 * Format date to IST
 * @param {string|Date} date - Date to format
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted date string in IST
 */
export const formatDateIST = (date, options = {}) => {
  if (!date) return '-';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    return d.toLocaleDateString('en-IN', {
      ...IST_OPTIONS,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      ...options,
    });
  } catch {
    return '-';
  }
};

/**
 * Format time to IST
 * @param {string|Date} date - Date to format
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted time string in IST
 */
export const formatTimeIST = (date, options = {}) => {
  if (!date) return '-';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    return d.toLocaleTimeString('en-IN', {
      ...IST_OPTIONS,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options,
    });
  } catch {
    return '-';
  }
};

/**
 * Format date and time to IST
 * @param {string|Date} date - Date to format
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted date and time string in IST
 */
export const formatDateTimeIST = (date, options = {}) => {
  if (!date) return '-';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    return d.toLocaleString('en-IN', {
      ...IST_OPTIONS,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options,
    });
  } catch {
    return '-';
  }
};

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '-';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return formatDateIST(date);
  } catch {
    return '-';
  }
};

/**
 * Format short date (e.g., "15 Mar")
 * @param {string|Date} date - Date to format
 * @returns {string} Short date string in IST
 */
export const formatShortDateIST = (date) => {
  if (!date) return '-';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    return d.toLocaleDateString('en-IN', {
      ...IST_OPTIONS,
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return '-';
  }
};

/**
 * Get current date in IST as YYYY-MM-DD string
 * @returns {string} Current date in IST
 */
export const getCurrentDateIST = () => {
  const now = new Date();
  return now.toLocaleDateString('en-CA', IST_OPTIONS); // en-CA gives YYYY-MM-DD format
};

export default {
  formatDateIST,
  formatTimeIST,
  formatDateTimeIST,
  formatRelativeTime,
  formatShortDateIST,
  getCurrentDateIST,
};
